import { useState } from 'react';
import { optIntoAnalytics, optOutOfAnalytics } from '../lib/analytics.ts';

const STORAGE_KEY = 'fantasy-manager:cookie-consent';

/** Fired on `window` the moment consent is decided, so `InstallPrompt`
 * (a sibling tree mounted in main.tsx, not a child of this component) can
 * notice without polling — see the "two bars" note on `InstallPrompt`. */
export const COOKIE_CONSENT_DECIDED_EVENT = 'fantasy-manager:cookie-consent-decided';

type Consent = 'accepted' | 'declined';

function storedConsent(): Consent | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'accepted' || raw === 'declined' ? raw : null;
}

/** Whether this banner is currently (or about to be, on mount) showing.
 * Exported so `InstallPrompt` can defer to it — see the "two bars" note
 * there for why consent wins the fixed-bottom-bar slot outright. */
export function isCookieConsentUndecided(): boolean {
  return storedConsent() === null;
}

/**
 * A non-blocking bottom bar, not a modal — this app deliberately doesn't use
 * modals anywhere else (see the "Edit my squad" panel, which replaces the
 * squad view in place rather than overlaying it), and a cookie banner
 * blocking the page is exactly the kind of thing that erodes trust in an
 * app that's asking permission to be tracked less, not more.
 *
 * Sentry (crash reporting) isn't gated behind this — it runs unconditionally
 * once configured, sends no PII by default, and is generally treated as
 * "strictly necessary" for keeping the app working, not "tracking". PostHog
 * (usage analytics) is the opt-in this banner is actually about.
 */
export function CookieBanner() {
  const [consent, setConsent] = useState<Consent | null>(storedConsent);

  if (consent !== null) return null;

  function decide(next: Consent) {
    localStorage.setItem(STORAGE_KEY, next);
    if (next === 'accepted') optIntoAnalytics();
    else optOutOfAnalytics();
    setConsent(next);
    window.dispatchEvent(new Event(COOKIE_CONSENT_DECIDED_EVENT));
  }

  return (
    <div className="cookie-banner rise-in" role="dialog" aria-labelledby="cookie-banner-heading">
      <div className="cookie-banner-body">
        <p id="cookie-banner-heading" className="cookie-banner-heading">
          Cookies &amp; analytics
        </p>
        <p className="hint">
          We use Sentry to catch bugs when something breaks — that runs regardless, sends no
          personal data, and isn't what this is asking about. With your OK, we'd also use PostHog
          to see how the app gets used (signups, which features get clicked) so we know what to
          improve. No ads, nothing sold on.
        </p>
      </div>
      <div className="row row--flush cookie-banner-actions">
        <button type="button" onClick={() => decide('accepted')}>
          Accept analytics
        </button>
        <button type="button" className="ghost" onClick={() => decide('declined')}>
          Decline
        </button>
      </div>
    </div>
  );
}
